import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';

registerLocale('fr-FR', fr);
import Button from '../../../../button';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from 'server';
import StyledInput from '../../../../styledInput/StyledInput';
import { dateToIsoString } from '../../../../../helper/helper';
import useUnassignReasons from '../../../../../hooks/useUnassignReasons';
import UnassignReasonForm from '../shared/UnassignReasonForm';
import { Calendar, WarningCircle } from '../../../../../assets/icons';
import {
  formatDateFr,
  getInterruptEndDateError,
  getReplacementRange,
} from 'helper/missionInterruptDates';

const TAB_SELECTED = 'border-2 border-yellow-500 bg-yellow-50';
const TAB_DEFAULT = 'border border-gray-200 bg-white';

const INTERRUPT_UNASSIGN_REASON_NAMES = ['No-show', "Erreur lors de l'attribution"];

const ModalMissionInterrupt = () => {
  const { toggle } = useModal();
  const { objID, setModalDataReturn, updateMissionStatusLocal, infoMission } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState('');
  const [endDate, setEndDate] = useState(new Date());
  const [duplicateMission, setDuplicateMission] = useState(false);
  const [missionData, setMissionData] = useState(null);
  const [isWaiting, setIsWaiting] = useState(true);
  const [isMissionStarted, setIsMissionStarted] = useState(false);
  const hasAssignedUser = !!infoMission?.user_assigned;
  const [action, setAction] = useState(hasAssignedUser ? 'unassign' : 'interrupt');

  const unassign = useUnassignReasons(
    hasAssignedUser ? objID : null,
    INTERRUPT_UNASSIGN_REASON_NAMES
  );

  useEffect(() => {
    if (objID) {
      setIsWaiting(true);
      ApiDatabase.getMissionDetailsById(
        { idMission: objID },
        (data) => setMissionData(data),
        () => setIsWaiting(false),
        { skipCache: true }
      );
    }
  }, [objID]);

  useEffect(() => {
    if (!missionData) return;

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const startStr = missionData.duration?.datestart?.slice(0, 10);
    setIsMissionStarted(startStr ? todayStr >= startStr : false);

    ApiDatabase.getReasonsMissionSuspension(
      { type: 'interruption' },
      (data) => {
        let sorted = [...data];
        sorted.sort((a, b) => (a.name === 'No-Show' ? -1 : b.name === 'No-Show' ? 1 : 0));
        setReasons(sorted);
        if (sorted.length > 0) setReasonSelected(sorted[0].name);
        setIsWaiting(false);
      },
      () => setIsWaiting(false)
    );
  }, [missionData]);

  useEffect(() => {
    setReasonSelected(reasons.length > 0 ? reasons[0].name : '');
    unassign.reset();
    setDuplicateMission(false);
  }, [action]);

  const isNoShow = reasonSelected === 'No-Show';
  const noShowError =
    isNoShow && !isMissionStarted
      ? 'Le motif "No-Show" ne peut être sélectionné qu\'une fois la mission commencée'
      : '';

  const missionDateEnd = missionData?.duration?.dateend ?? null;

  const { start: duplicateDateStart, end: duplicateDateEnd } = useMemo(
    () => getReplacementRange({ endDate, missionDateEnd }),
    [endDate, missionDateEnd]
  );

  const endDateError = getInterruptEndDateError({
    endDate,
    missionDateEnd,
    isNoShow,
  });

  const isInterruptFormValid =
    !!reasonSelected && !noShowError && !endDateError && !isWaiting && (isNoShow || !!endDate);

  const isFormValid = action === 'interrupt' ? isInterruptFormValid : (!!unassign.selectedId && !unassign.noShowError);
  const isCurrentWaiting = action === 'interrupt' ? isWaiting : unassign.isLoading;

  const handleSubmitInterrupt = () => {
    if (!objID || !reasonSelected) {
      toast.error('Veuillez sélectionner un motif');
      return;
    }
    if (noShowError) return;

    setIsLoading(true);
    const payload = { idMission: objID, newStatus: 'interrupted', reason: reasonSelected, duplicateMission };
    if (!isNoShow) {
      payload.endDate = dateToIsoString(endDate);
      if (duplicateMission) {
        payload.duplicateDateStart = dateToIsoString(duplicateDateStart);
        payload.duplicateDateEnd = duplicateDateEnd ? dateToIsoString(duplicateDateEnd) : undefined;
      }
    }

    ApiDatabase.updateMissionStatus(
      payload,
      () => {
        if (isNoShow && duplicateMission) {
          updateMissionStatusLocal(objID, {
            user_assigned: null,
            user: null,
            isAssigned: false,
          });
        } else if (isNoShow) {
          updateMissionStatusLocal(objID, { status: 'canceled', reason: reasonSelected });
        } else {
          updateMissionStatusLocal(objID, { status: 'interrupted', reason: reasonSelected });
        }
        setModalDataReturn('mission_status_updated');
        toast.success('La mission a été interrompue');
        setIsLoading(false);
        toggle();
      },
      (err) => {
        toast.error("Erreur lors de l'interruption : " + err);
        setIsLoading(false);
      }
    );
  };

  const handleSubmitUnassign = () => {
    if (!objID || !unassign.selectedId || !infoMission?.user_assigned) {
      toast.error('Veuillez sélectionner un motif');
      return;
    }
    if (unassign.noShowError) return;
    setIsLoading(true);
    ApiDatabase.unassignMission(
      {
        idMission: objID,
        userAssigned: infoMission.user_assigned,
        reason: unassign.selectedId,
        subReasons: unassign.subReasonsSelected.length > 0 ? unassign.subReasonsSelected : null,
      },
      () => {
        updateMissionStatusLocal(objID, {
          user_assigned: null,
          user: null,
          isAssigned: false,
        });
        setModalDataReturn('mission unassigned');
        toast.success("L'intérimaire a été désattribué");
        setIsLoading(false);
        toggle();
      },
      (err) => {
        toast.error('Erreur lors de la désattribution : ' + (err.response?.data?.error || err));
        setIsLoading(false);
      }
    );
  };

  const headerTitle = () => {
    if (!hasAssignedUser) return 'Interrompre la mission';
    if (action === 'unassign') return "Désattribuer l'intérimaire";
    return 'Interrompre la mission';
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {headerTitle()}
      </Modalheader>
      <ModalBody>
        {hasAssignedUser && (
          <div className="flex flex-col gap-1 mb-4">
            <button
              className={`w-full text-left p-4 rounded-none transition-colors ${action === 'unassign' ? TAB_SELECTED : TAB_DEFAULT}`}
              onClick={() => setAction('unassign')}
            >
              <span className="ft-sm font-medium text-gray-700">Dés-attribuer l'intérimaire</span>
              <p className="ft-sm text-gray-500 mt-1">La mission restera en ligne pour être attribuée à quelqu'un d'autre.</p>
            </button>
            <button
              className={`w-full text-left p-4 rounded-none transition-colors ${action === 'interrupt' ? TAB_SELECTED : TAB_DEFAULT}`}
              onClick={() => setAction('interrupt')}
            >
              <span className="ft-sm font-medium text-gray-700">Interrompre la mission</span>
            </button>
          </div>
        )}

        {isCurrentWaiting ? (
          <div className="flex flex-col gap-3 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-10 bg-gray-200 rounded w-full" />
          </div>
        ) : action === 'interrupt' ? (
          <div className="flex flex-col gap-3">
            <StyledInput
              type="select"
              label="Motif"
              placeholder="Sélectionnez un motif"
              value={reasonSelected}
              onChange={(e) => setReasonSelected(e.target.value)}
              list={reasons.map((r) => ({ value: r.name, label: r.name }))}
            />
            {noShowError && (
              <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
                {noShowError}
              </div>
            )}
            {!isNoShow && (
              <div>
                <label className="ft-sm text-gray-500 block">
                  Interrompre la mission le
                  <div className="relative mt-1">
                    <DatePicker
                      dateFormat="dd/MM/yyyy"
                      locale="fr-FR"
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      peekNextMonth showMonthDropdown showYearDropdown
                      dropdownMode="select"
                      popperPlacement="auto"
                      popperProps={{ strategy: 'fixed' }}
                      popperClassName="z-[9999]"
                      className="w-full h-10 pl-10 pr-3 ft-b border rounded-lg text-left text-[14px] cursor-pointer"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center px-2.5 pointer-events-none">
                      <Calendar wh={18} color="#6B7280" />
                    </div>
                  </div>
                </label>
                {endDateError && (
                  <div className="text-red-500 flex items-center gap-1 text-xs mt-1">
                    <WarningCircle wh={16} color="#EF4444" />
                    {endDateError}
                  </div>
                )}
              </div>
            )}
            <label className="flex gap-3 items-start border border-gray-200 rounded-none p-6 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={duplicateMission}
                onChange={(e) => setDuplicateMission(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">Remplacer l'intérimaire</span>
                <span className="text-sm text-gray-500">
                  Le client a demandé à ce que l'intérimaire soit remplacé pour cette mission
                  {!isNoShow && ` du ${formatDateFr(duplicateDateStart)} au ${formatDateFr(duplicateDateEnd)}`}
                </span>
              </div>
            </label>
          </div>
        ) : (
          <UnassignReasonForm unassign={unassign} />
        )}
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>Fermer</Button>
        {isLoading ? (
          <Button size="LG" color="DISABLED">Confirmation ...</Button>
        ) : (
          <Button
            size="LG"
            color={isFormValid ? 'PRIMARY' : 'DEFAULT'}
            onClick={action === 'interrupt' ? handleSubmitInterrupt : handleSubmitUnassign}
            disabled={!isFormValid || isCurrentWaiting}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionInterrupt;
